import { ComposeEmailModal } from '@/components/molecules/ComposeEmailModal';
import { FilterQueryType } from '@/modules/dashboard/dashboardTypes';
import {
	DashedLine,
	EnrollIcon,
	Envelope,
	PlaneGray,
	IdleIcon,
	PaperIcon,
	PlayIcon
} from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { useMemo, useState } from 'react';
import Button from '@/components/Button/newButtons';
import { Tooltip } from '@/components/atoms/Tooltip';
import { Popover } from '@/components/atoms/Popover';
import { Link } from 'react-router-dom';
import { Dialog } from '@/components/atoms/Dialog';
import { UserRole } from '@/modules/user/userTypes';
import { Table } from '@/components/atoms/Table';
import type { SequencesType } from '@/modules/sequencesRedesign/hooks/useSequences';
import { cn } from '@/lib/classNames';
import { useUserData } from '../../../hooks/useUserData';
import { useSelectListMutation } from './src/hooks/useSelectListMutation';
import { useSelectedBusinessData } from '../../../hooks/useSelectedBusinessData';
import { useListsDetailsParams } from '../../../hooks/useListsDetailsParams';
import { useListDetails } from '../../../hooks/useListDetails';
import { useQuerySequences } from './src/hooks/useQuerySequences';
import { useListDetailsFiltersContext } from '../../../providers/ListDetailsFiltersProviders';

type GetFirstAndLastIndexProps = {
	currentPage: number;
	limit: number;
	totalElements: number;
};

const getFirstAndLastIndex = ({
	currentPage,
	limit,
	totalElements
}: GetFirstAndLastIndexProps) => {
	const displayFirstIndex = (currentPage - 1) * limit + 1;
	const isLastPageElementsListSmallerThenTotalElements =
		currentPage * limit < totalElements;
	const displayLastIndex = isLastPageElementsListSmallerThenTotalElements
		? currentPage * limit
		: totalElements;

	return { first: displayFirstIndex, last: displayLastIndex };
};

type BusinessHeaderProps = {
	currentPage: number;
	totalElements: number;
};

export const BusinessHeader = ({
	currentPage,
	totalElements
}: BusinessHeaderProps) => {
	const { listId } = useListsDetailsParams();

	const { appliedFilters, onFiltersChange } = useListDetailsFiltersContext();

	const { user } = useUserData();

	const { mutate: mutateList } = useSelectListMutation();

	const { selectedBusiness } = useSelectedBusinessData();
	const { first, last } = getFirstAndLastIndex({
		currentPage,
		limit: appliedFilters?.limit || 50,
		totalElements
	});

	const { list } = useListDetails({ id: listId });

	const isUserFreeTier =
		user?.role === UserRole.Free || user?.role === UserRole.PreTrial;

	const [openEmailModal, setOpenEmailModal] = useState(false);

	const handleCloseModal = () => {
		setOpenEmailModal(false);
	};

	const handleComposeEmailButtonClick = () => {
		onFiltersChange({
			limit: 50,
			skip: 0,
			...appliedFilters,
			revertIncludeEmailsYes: false,
			includeEmailsYes: true,
			count: true,
			queryType: FilterQueryType.FILTER
		});

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Compose Email button pressed in list view'
			});
		}

		mutateList(listId);

		setOpenEmailModal(true);
	};

	const { data } = useQuerySequences();

	const sequencesData = data?.data.data.campaigns;

	const newSequenceButtonPath = useMemo(() => {
		if (isUserFreeTier) {
			return `/sequences?state=MY-SEQUENCES&id=${appliedFilters?.listId}&showAiSequenceModal=true`;
		}

		return `/sequences?state=SEQUENCE-START&id=${appliedFilters?.listId}`;
	}, [appliedFilters?.listId, isUserFreeTier]);

	return (
		<>
			<header className="flex w-full items-end gap-4 rounded-md bg-dm-black p-2">
				{!isUserFreeTier && (
					<Button
						onClick={handleComposeEmailButtonClick}
						leftIcon={<Envelope />}
						disabled={totalElements === 0}
					>
						Compose Email
					</Button>
				)}

				<Tooltip.Provider>
					<Tooltip.Root>
						<Tooltip.Trigger>
							<Popover.Root>
								<Popover.Trigger className="h-fit border-none p-0">
									<Button
										disabled={totalElements === 0}
										leftIcon={
											<PlaneGray className="[&_path]:fill-dm-black" />
										}
									>
										Sequence
									</Button>
								</Popover.Trigger>
								<Popover.Content
									side="top"
									align="start"
									sideOffset={10}
									className="grid gap-2 p-2"
								>
									<Link to={newSequenceButtonPath}>
										<div className="w-full rounded-md bg-dm-black p-4 text-left text-sm font-semibold duration-300 hover:bg-dm-black3.5">
											<div className="flex items-center gap-2">
												<PlaneGray className="[&_path]:fill-dm-gold" />
												<span className="text-dm-gold">
													Create new sequence
												</span>
											</div>

											{isUserFreeTier ? (
												<p className="mt-2.5 text-pretty">
													Build an email sequence in
													seconds with AI-crafted
													messaging
												</p>
											) : (
												<p className="mt-2.5 text-pretty">
													Create a new sequence from{' '}
													<br /> scratch or use an
													existing template or <br />
													generate with our AI
												</p>
											)}
										</div>
									</Link>
									{sequencesData &&
										sequencesData?.length > 0 && (
											<>
												<DashedLine className="my-2" />

												<Dialog.Root>
													<Dialog.Trigger>
														<button
															type="button"
															className="h-full w-full rounded-md bg-dm-black duration-300 hover:bg-dm-black3.5"
														>
															<div className="p-4 text-left text-sm font-semibold">
																<div className="flex items-center gap-2">
																	<EnrollIcon />
																	<span className="text-dm-gold">
																		Add to
																		an
																		existing
																		sequence
																	</span>
																</div>

																<p className="mt-2.5 text-pretty">
																	Enroll this
																	list to an
																	existing{' '}
																	<br />
																	sequence
																</p>
															</div>
														</button>
													</Dialog.Trigger>

													<Dialog.Content className="max-w-screen-lg bg-dm-black1 px-10 pb-8 pt-12 [&>button]:right-12 [&>button]:top-10 [&>button]:scale-[2]">
														<Dialog.Header className="gap-0">
															<Dialog.Title className="text-lg">
																Choose a
																Sequence to
																enroll this List
															</Dialog.Title>

															<Dialog.Description className="sr-only">
																Select a
																sequence to
																enroll this list
																to.
															</Dialog.Description>

															<div className="border-b-[1px] border-dm-gray pb-4 text-sm font-semibold text-dm-strong-mid-grey">
																You&apos;re
																enrolling:{' '}
																<span className="text-dm-gold">
																	{list?.name}
																</span>{' '}
																(
																{
																	list
																		?.businessIds
																		.length
																}{' '}
																businesses)
															</div>

															{sequencesData && (
																<BusinessSequencesTable
																	sequences={
																		sequencesData
																	}
																/>
															)}
														</Dialog.Header>
													</Dialog.Content>
												</Dialog.Root>
											</>
										)}
								</Popover.Content>
							</Popover.Root>
						</Tooltip.Trigger>
					</Tooltip.Root>
				</Tooltip.Provider>

				<span className="block h-full w-px bg-dm-gray" />

				<p className="whitespace-nowrap text-dm-white">
					{first}-{last} of {totalElements} businesses
				</p>
			</header>

			<ComposeEmailModal
				isOpen={openEmailModal}
				onCloseModal={handleCloseModal}
				userEmail={user.email}
				businessSelected={selectedBusiness || undefined}
				listId={listId}
				appliedFilters={appliedFilters}
				onFilterChange={onFiltersChange}
			/>
		</>
	);
};

type BusinessSequencesTableProps = {
	sequences: SequencesType[];
};

export const BusinessSequencesTable = ({
	sequences
}: BusinessSequencesTableProps) => {
	const { listId } = useListsDetailsParams();

	const getTooltipData = (status: SequencesType['status']) => {
		switch (status) {
			case 'ACTIVE':
				return {
					icon: <PlayIcon />,
					message: (
						<>
							This sequence is currently in
							<br />
							progress and actively engaging
							<br />
							businesses.
						</>
					)
				};
			case 'DRAFT':
				return {
					icon: <PaperIcon />,
					message: (
						<>
							This is a draft sequence saved
							<br />
							from your last edit session.
							<br />
							Complete the setup to activate it.
						</>
					)
				};
			case 'IDLE':
				return {
					icon: <IdleIcon />,
					message: (
						<>
							This sequence currently has no
							<br />
							active processes.
						</>
					)
				};
			default:
				return null;
		}
	};

	return (
		<Table.Root className="relative mt-4 min-h-[50dvh] rounded-lg border-2 border-dm-grey">
			<Table.Header>
				<Table.Row className="h-10 font-semibold">
					<BorderedTh>Name</BorderedTh>
					<BorderedTh className="text-center">Steps</BorderedTh>
					<BorderedTh className="text-center">Status</BorderedTh>
					<BorderedTh>Last Edit</BorderedTh>
					<BorderedTh>
						<span className="sr-only">Action</span>
					</BorderedTh>
				</Table.Row>
			</Table.Header>

			<Table.Body columns={sequences.length}>
				{sequences.map(sequence => {
					const tooltipData = getTooltipData(sequence.status);

					return (
						<Table.Row key={sequence.id}>
							<BorderedTd>{sequence.name}</BorderedTd>

							<BorderedTd className="text-center">
								{sequence.steps.length}
							</BorderedTd>

							<BorderedTd>
								<div className="flex items-center justify-center gap-3">
									{tooltipData && (
										<Tooltip.Provider>
											<Tooltip.Root>
												<Tooltip.Trigger>
													{tooltipData.icon}
												</Tooltip.Trigger>
												<Tooltip.Content>
													{tooltipData.message}
												</Tooltip.Content>
											</Tooltip.Root>
										</Tooltip.Provider>
									)}
								</div>
							</BorderedTd>

							<BorderedTd>
								{sequence.updatedAt &&
									new Date(
										sequence.updatedAt
									).toLocaleDateString('en-US', {
										month: 'long',
										day: '2-digit',
										year: 'numeric'
									})}
							</BorderedTd>

							<BorderedTd>
								<Link
									to={`/sequences?state=ADD-BUSINESS&id=${listId}&sequenceId=${sequence.id}`}
								>
									<Button
										className="ml-auto"
										variant="secondary"
									>
										Enroll
									</Button>
								</Link>
							</BorderedTd>
						</Table.Row>
					);
				})}
			</Table.Body>
		</Table.Root>
	);
};

const BorderedTh = ({
	children,
	className
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	const Component = Table.Head;

	return (
		<Component
			className={cn('border-b-2 !border-dm-gray bg-dm-black1', className)}
		>
			{children}
		</Component>
	);
};

const BorderedTd = ({
	children,
	className
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	const Component = Table.Cell;

	return (
		<Component className={cn('border-b-2 !border-dm-gray', className)}>
			{children}
		</Component>
	);
};
